import clsx from "clsx";
import { motion, type MotionValue } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useCountUp } from "hooks/useCountUp";
import { usePresaleHeroData } from "hooks/usePresaleHeroData";
import {
  AnimatedGradientBorder,
  GlowCard,
  GlassGlow,
  HeroBackdrop,
  NoiseTexture,
} from "../shared";

import ethLogo from "assets/coins/eth.png";
import bnbLogo from "assets/coins/bnb.png";
import usdcLogo from "assets/coins/usdc.png";
import bnbChainLogo from "assets/membersLogo/BnbChain.png";
import coinmarketcapLogo from "assets/membersLogo/Coinmarketcap.png";
import yziLabsLogo from "assets/membersLogo/YZILabs.png";

const usdtLogo = process.env.PUBLIC_URL + "/coins/usdt.webp";

interface NewHeroSectionProps {
  heroOpacity: MotionValue<number>;
  heroScale: MotionValue<number>;
}

const TIMER_UNITS = [
  { key: "d", label: "Days" },
  { key: "h", label: "Hrs" },
  { key: "m", label: "Min" },
  { key: "s", label: "Sec" },
] as const;

const PAYMENT_TOKENS = [
  { name: "ETH", logo: ethLogo },
  { name: "BNB", logo: bnbLogo },
  { name: "USDT", logo: usdtLogo },
  { name: "USDC", logo: usdcLogo },
];

export function NewHeroSection({
  heroOpacity,
  heroScale,
}: NewHeroSectionProps) {
  const presale = usePresaleHeroData();
  const stat1 = useCountUp(167267);
  const stat2 = useCountUp(105500);
  const stat3 = useCountUp(98909);

  const stats = [
    { label: "Average App Rating", value: "4.5", suffix: "/5", ref: undefined },
    {
      label: "Telegram Community",
      value: stat1.count.toLocaleString(),
      ref: stat1.ref,
    },
    {
      label: "X Followers",
      value: stat2.count.toLocaleString(),
      ref: stat2.ref,
    },
    {
      label: "Discord Members",
      value: stat3.count.toLocaleString(),
      ref: stat3.ref,
    },
  ];

  return (
    <section
      id="hero"
      className="min-h-screen pt-28 pb-12 md:pt-32 md:pb-16 px-6 md:px-12 lg:px-20 relative flex flex-col justify-center"
    >
      <HeroBackdrop />

      <motion.div
        style={{ opacity: heroOpacity, scale: heroScale }}
        className="max-w-7xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start relative z-10"
      >
        <HeroContent />

        <PresaleCard presale={presale} />
      </motion.div>

      <StatsBar stats={stats} />
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Hero left column                                                   */
/* ------------------------------------------------------------------ */

function HeroContent() {
  return (
    <div className="flex flex-col gap-6">
      <motion.h1
        className="font-space-grotesk text-5xl md:text-6xl lg:text-[4rem] font-bold leading-[1.08] tracking-[-0.03em]"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15 }}
      >
        <span className="shimmer-text">The AI agent hub</span>
        <br />
        <span className="shimmer-text">built for crypto.</span>
        <br />
        <span className="gradient-text-dark">Get $ATTS before launch.</span>
      </motion.h1>

      <motion.p
        className="text-lg md:text-xl text-white/60 leading-relaxed max-w-lg"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.35 }}
      >
        A personal AI command center with plug-and-play agents for DeFi,
        trading, social, and payments. $ATTS powers every agent, transaction,
        and the built-in neobank.
      </motion.p>

      <motion.div
        className="flex flex-col gap-4 mt-1"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.5 }}
      >
        <a
          href="https://x.com/agnt_hub/status/1948742398177128788"
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-wrap gap-3 items-center cursor-pointer hover:opacity-90 transition-opacity"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.15em]">
            <span className="text-white/70">MVB10 Member</span>
            <span className="text-white/40">, accelerated by</span>
          </span>
          <div className="flex items-center flex-wrap gap-4">
            <img
              src={bnbChainLogo}
              alt="BNB Chain"
              className="h-6 object-contain opacity-80 hover:opacity-100 transition-opacity duration-500"
            />
            <img
              src={coinmarketcapLogo}
              alt="CoinMarketCap"
              className="h-5 object-contain invert opacity-70 hover:opacity-100 transition-opacity duration-500"
            />
            <img
              src={yziLabsLogo}
              alt="YZi Labs"
              className="h-5 object-contain opacity-80 hover:opacity-100 transition-opacity duration-500"
            />
          </div>
        </a>
      </motion.div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Presale card (right column)                                        */
/* ------------------------------------------------------------------ */

function PresaleCard({
  presale,
}: {
  presale: ReturnType<typeof usePresaleHeroData>;
}) {
  const { timer, pad, prices, progress, raisedLabel, poolLabel } = presale;
  return (
    <motion.div
      className="relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.2 }}
    >
      <AnimatedGradientBorder className="rounded-[2rem]">
        <GlowCard className="bg-[#111111] p-4 md:p-9 rounded-[2rem] flex flex-col gap-8 relative">
          <GlassGlow intensity={0.14} size={75} />
          <NoiseTexture />

          {/* Header */}
          <div className="flex justify-between items-center relative z-10">
            <h3 className="font-space-grotesk text-2xl font-bold tracking-tight">
              $ATTS Presale
            </h3>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-yellow/10 border border-yellow/30">
              <motion.div
                className="w-1.5 h-1.5 rounded-full bg-yellow"
                animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.3, 1] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <span className="text-[10px] font-semibold tracking-[0.12em] uppercase text-yellow">
                Active
              </span>
            </div>
          </div>

          {/* Pricing grid */}
          <div className="grid grid-cols-3 gap-4 border-y w-full border-white/[0.06] py-6 relative z-10">
            <div className="w-full">
              <p className="text-white/50 text-[10px] uppercase tracking-[0.15em] mb-1.5 font-bold">
                Current
              </p>
              <p className="font-space-grotesk text-xl font-bold tabular-nums">
                {prices.current}
              </p>
            </div>
            <div className="w-full">
              <p className="text-white/50 text-[10px] uppercase tracking-[0.15em] mb-1.5 font-bold">
                Next
              </p>
              <p
                className={clsx(
                  "font-space-grotesk text-xl font-bold tabular-nums",
                  prices.next === "—" ? "text-white/50" : "text-white/80",
                )}
              >
                {prices.next}
              </p>
            </div>
            <div className="w-full">
              <p className="text-white/50 text-[10px] uppercase tracking-[0.15em] mb-1.5 font-bold">
                Listing
              </p>
              <p
                className={clsx(
                  "font-space-grotesk text-xl font-bold tabular-nums",
                  prices.listing === "—" ? "text-white/50" : "text-white/80",
                )}
              >
                {prices.listing}
              </p>
            </div>
          </div>

          {/* Timer */}
          <div className="flex flex-col gap-3 relative z-10">
            <span className="text-[10px] font-bold text-white/50 uppercase tracking-[0.15em]">
              Round ends in
            </span>
            <div className="flex gap-2.5 font-space-grotesk">
              {TIMER_UNITS.map((t) => {
                const val = pad(timer[t.key]);
                return (
                  <div
                    key={t.label}
                    className="bg-white/[0.04] border border-white/[0.06] py-3 rounded-xl flex-1 text-center"
                  >
                    <span className="text-2xl font-bold block mb-0.5">
                      {val}
                    </span>
                    <span className="text-[9px] text-white/50 uppercase tracking-[0.15em] font-sans font-bold">
                      {t.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Progress bar */}
          <div className="flex flex-col gap-2.5 relative z-10">
            <div className="flex justify-between text-sm font-medium">
              <span className="text-white/50 tracking-wider uppercase text-[10px] font-bold">
                USD Raised
              </span>
              <span className="font-space-grotesk text-base tabular-nums">
                {raisedLabel}{" "}
                <span className="text-white/50">/ {poolLabel}</span>
              </span>
            </div>
            <div className="h-2 bg-white/[0.06] rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.4) 100%)",
                }}
                initial={{ width: "0%" }}
                animate={{ width: `${progress.percent}%` }}
                transition={{
                  duration: 2.5,
                  delay: 0.8,
                  ease: [0.22, 1, 0.36, 1],
                }}
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  animate={{ x: ["-100%", "200%"] }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    repeatDelay: 3,
                  }}
                />
              </motion.div>
            </div>
          </div>

          {/* CTA button */}
          <motion.a
            href="/dashboard"
            className="bg-white text-black py-4 rounded-full font-bold text-base w-full flex items-center justify-center gap-2.5 relative z-10 no-underline"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Get $ATTS Token
            <motion.span
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowRight size={18} />
            </motion.span>
          </motion.a>

          {/* Payment tokens & links */}
          <div className="flex flex-col items-center gap-4 relative z-10">
            <div className="flex gap-2">
              {PAYMENT_TOKENS.map((token) => (
                <span
                  key={token.name}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold pl-1.5 pr-3 py-1.5 bg-white/[0.04] rounded-full text-white/40 border border-white/[0.06] hover:bg-white/[0.08] transition-colors cursor-pointer"
                >
                  <img
                    src={token.logo}
                    alt={token.name}
                    className="w-4 h-4 rounded-full"
                  />
                  {token.name}
                </span>
              ))}
            </div>
            <div className="flex gap-5 text-sm font-medium text-white/50">
              <a
                href="https://t.me/agnthub_support_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white/60 transition-colors"
              >
                How to buy
              </a>
              <span className="text-white/10">|</span>
              <a
                href="/referrals"
                className="hover:text-white/60 transition-colors"
              >
                Invite friends
              </a>
            </div>
          </div>
        </GlowCard>
      </AnimatedGradientBorder>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Stats bar                                                          */
/* ------------------------------------------------------------------ */

interface StatItem {
  label: string;
  value: string;
  suffix?: string;
  ref: React.RefObject<HTMLDivElement> | undefined;
}

function StatsBar({ stats }: { stats: StatItem[] }) {
  return (
    <div className="max-w-7xl mx-auto w-full mt-16 relative z-10">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/[0.04] rounded-2xl overflow-hidden border border-white/[0.06]">
        {stats.map((stat) => (
          <div
            key={stat.label}
            ref={stat.ref}
            className="bg-[#0a0a0a] p-6 md:p-8 text-center flex flex-col gap-2"
          >
            <span className="text-[10px] font-bold text-white/50 uppercase tracking-[0.15em]">
              {stat.label}
            </span>
            <span className="font-space-grotesk text-2xl md:text-3xl font-bold tabular-nums gradient-text-bright">
              {stat.value}
              {stat.suffix ?? ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
